import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Plus, RefreshCw, Video } from "lucide-react";
import { api } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import Dialog from "../components/Dialog";
import ContactDrawer, { BookingActions } from "../components/crm/ContactDrawer";
import useNotice from "../components/crm/useNotice";
import type { BookingRow, Calendar, Person, WeeklyRange } from "../components/crm/types";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { callStatusLabel, contactName, shortWhen, stageLabel, tzLabel, wasRescheduled } from "../crm";
import { useContactParam } from "./CrmPipeline";

type Google = { allowed: boolean; hosts_with_calendar?: number[]; calendars?: { id: string; name: string; primary: boolean }[]; error?: string };

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "Pacific/Honolulu"];
const LENGTHS = [15, 20, 30, 45, 60, 90];
const NOTICE = [{ v: 0, l: "No minimum" }, { v: 60, l: "1 hour" }, { v: 120, l: "2 hours" }, { v: 240, l: "4 hours" }, { v: 720, l: "12 hours" }, { v: 1440, l: "1 day" }, { v: 2880, l: "2 days" }];
const AHEAD = [7, 14, 21, 30, 60, 90];
/** "Mon to Fri, 9:00 AM to 5:00 PM" when every open day shares hours, else the days listed. */
function hoursText(hours: WeeklyRange[]) {
  if (!hours.length) return "No hours";
  const t = (s: string) => { const [h, m] = s.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
  const same = hours.every((r) => r.start === hours[0].start && r.end === hours[0].end);
  const days = [...new Set(hours.map((r) => r.day))].sort();
  const run = days.length > 2 && days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  const dayText = days.length === 7 ? "Every day" : run ? `${DAYS[days[0]]} to ${DAYS[days[days.length - 1]]}` : days.map((d) => DAYS[d]).join(", ");
  return same ? `${dayText}, ${t(hours[0].start)} to ${t(hours[0].end)}` : dayText;
}

/** Booking calendars (the hours people can book) and the calls booked on them. */
export default function CrmBookings() {
  const calendars = useApi<Calendar[]>("/api/crm/calendars");
  const google = useApi<Google>("/api/crm/google");
  const [when, setWhen] = useState<"upcoming" | "past" | "cancelled">("upcoming");
  const bookings = useApi<BookingRow[]>(`/api/crm/bookings?when=${when}`);
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [editing, setEditing] = useState<Calendar | "new" | null>(null);
  const [contactId, openContact] = useContactParam();
  const [notice, setNotice] = useNotice();
  const newCalendar = canWrite && <button className="button" onClick={() => setEditing("new")}><Plus aria-hidden="true" />New calendar</button>;
  const g = google.data;
  const manyCalendars = (calendars.data || []).length > 1;
  // A calendar reaches Google through its host's own Google Calendar, else the shared connection.
  const offline = g ? (calendars.data || []).filter((c) => c.active && !g.allowed && !(c.host_user_id && g.hosts_with_calendar?.includes(c.host_user_id))) : [];
  // A rescheduled call's old time is cancelled under the hood, but it was moved, not
  // called off: Cancelled lists only calls that were called off. The new time is under Upcoming.
  const calls = (bookings.data || []).filter((b) => when !== "cancelled" || !wasRescheduled(b));

  return (
    <>
      <PageHeader title="Bookings" actions={newCalendar} />
      {offline.length > 0 && (
        <p className="inline-notice crm-notice">
          <span>
            {offline.length === 1 ? `${offline[0].name} is` : `${offline.length} calendars are`} not linked to a Google Calendar, so {offline.length === 1 ? "it uses" : "they use"} the hours below and no invite is sent.
            {" "}The host connects their Google calendar in <Link to="/settings#my-google">Settings</Link>.
          </span>
        </p>
      )}
      {g?.error && <p className="inline-notice danger">{g.error}</p>}
      {notice && <p className="inline-notice" role="status">{notice}</p>}

      <section className="panel table-wrap">
        <div className="crm-panel-head"><h2>Calendars</h2></div>
        {calendars.loading && !calendars.data ? <PageSkeleton cards={0} />
          : calendars.error ? <ErrorState message={calendars.error} onRetry={calendars.reload} />
          : !(calendars.data || []).length ? <EmptyState title="No calendars yet" description="A calendar sets when people can book you. Add one, then pick it on a form's After submit tab." action={newCalendar || undefined} />
          : (
            <table className="module-table crm-table">
              <thead><tr><th>Calendar</th><th>Status</th><th>Hours</th><th className="num">Length</th><th>Host</th><th className="num">Upcoming</th><th>Booking page</th></tr></thead>
              <tbody>
                {(calendars.data || []).map((c) => (
                  <tr key={c.id} className={canWrite ? "is-clickable" : undefined} onClick={() => canWrite && setEditing(c)}>
                    <td data-label="Calendar">{canWrite ? <button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); setEditing(c); }}>{c.name}</button> : c.name}<small className="crm-muted crm-block">/book/{c.slug}</small></td>
                    <td data-label="Status"><span className={`crm-status${c.active ? " live" : ""}`} title={c.active ? "Taking bookings" : "Not taking bookings"}>{c.active ? "On" : "Off"}</span></td>
                    <td data-label="Hours">{hoursText(c.weekly_hours)}<small className="crm-muted crm-block">{tzLabel(c.timezone)} time</small></td>
                    <td data-label="Length" className="num">{c.duration_min} min</td>
                    <td data-label="Host">{c.host_name || <span className="crm-muted">None</span>}</td>
                    <td data-label="Upcoming" className="num">{c.upcoming}</td>
                    <td data-label="Booking page" className="crm-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <span className="crm-inline-actions">
                        <a className="icon-button" href={`/book/${c.slug}`} target="_blank" rel="noreferrer" aria-label={`Open ${c.name} booking page`} title="Open booking page"><ExternalLink aria-hidden="true" /></a>
                        <button type="button" className="icon-button" aria-label={`Copy ${c.name} link`} title="Copy link" onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/book/${c.slug}`); setNotice(`Link copied: ${window.location.origin}/book/${c.slug}`); }}><Copy aria-hidden="true" /></button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>

      <section className="panel table-wrap crm-stack">
        <div className="crm-panel-head">
          <h2>Calls</h2>
          <div className="tabs" role="tablist" aria-label="Which calls">
            {(["upcoming", "past", "cancelled"] as const).map((w) => <button key={w} role="tab" aria-selected={when === w} className={when === w ? "active" : ""} onClick={() => setWhen(w)}>{w === "upcoming" ? "Upcoming" : w === "past" ? "Past" : "Cancelled"}</button>)}
          </div>
          <button className="icon-button toolbar-end" aria-label="Refresh calls" title="Refresh calls" onClick={bookings.reload}><RefreshCw className={bookings.refreshing ? "spin" : ""} aria-hidden="true" /></button>
        </div>
        {bookings.loading && !bookings.data ? <PageSkeleton cards={0} />
          : bookings.error ? <ErrorState message={bookings.error} onRetry={bookings.reload} />
          : !calls.length ? <EmptyState title={when === "upcoming" ? "No calls booked" : when === "past" ? "No past calls" : "No cancelled calls"} description={when === "upcoming" ? "Calls booked from your forms and booking pages show up here." : "Nothing here yet."} />
          : (
            <table className="module-table crm-table">
              <thead><tr>
                <th>When</th><th>Contact</th>{manyCalendars && <th>Calendar</th>}<th>Deal stage</th>
                {when === "past" && <th>Status</th>}{when === "cancelled" && <th>Reason</th>}
                <th><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {calls.map((b) => (
                  <tr key={b.id} className={b.contact_id ? "is-clickable" : undefined} onClick={() => b.contact_id && openContact(b.contact_id)}>
                    <td data-label="When">{shortWhen(b.starts_at)}</td>
                    <td data-label="Contact">{b.contact_id ? <button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); openContact(b.contact_id!); }}>{contactName({ first_name: b.first_name, last_name: b.last_name, email: b.email, company: b.company })}</button> : "Removed"}{b.company && <small className="crm-muted crm-block">{b.company}</small>}</td>
                    {manyCalendars && <td data-label="Calendar">{b.calendar_name}</td>}
                    <td data-label="Deal stage" data-empty={b.stage ? undefined : ""}>{b.stage ? <span className={`crm-stage s-${b.stage}`}>{stageLabel(b.stage)}</span> : <span className="crm-muted">—</span>}</td>
                    {when === "past" && <td data-label="Status"><span className={`crm-call-status s-${b.status}`}>{callStatusLabel(b.status, b.ends_at)}</span></td>}
                    {when === "cancelled" && <td data-label="Reason" className="crm-muted" data-empty={b.cancel_reason ? undefined : ""}>{b.cancel_reason}</td>}
                    <td data-label="" className="crm-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <span className="crm-inline-actions">
                        {b.meeting_url && b.status === "booked" && <a className="icon-button" href={b.meeting_url} target="_blank" rel="noreferrer" aria-label="Open the video call" title="Video call"><Video aria-hidden="true" /></a>}
                        {canWrite && <BookingActions id={b.id} status={b.status} past={new Date(b.starts_at).getTime() < Date.now()} onDone={() => { bookings.reload(); calendars.reload(); }} onReschedule={b.contact_id ? () => openContact(b.contact_id!, "book", b.id) : undefined} />}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>

      {editing && (
        <CalendarEditor
          calendar={editing === "new" ? null : editing}
          google={g}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); setNotice(msg); calendars.reload(); }}
        />
      )}
      {contactId && <ContactDrawer key={contactId} id={contactId} onClose={() => openContact(null)} onChanged={() => { bookings.reload(); calendars.reload(); }} />}
    </>
  );
}

function CalendarEditor({ calendar, google, onClose, onSaved }: { calendar: Calendar | null; google: Google | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const people = useApi<Person[]>("/api/tasks/assignees");
  const { user } = useAuth();
  const [f, setF] = useState(() => ({
    name: calendar?.name || "Intro call",
    slug: calendar?.slug || "",
    description: calendar?.description || "",
    duration_min: calendar?.duration_min || 30,
    buffer_before_min: calendar?.buffer_before_min ?? 0,
    buffer_after_min: calendar?.buffer_after_min ?? 15,
    min_notice_min: calendar?.min_notice_min ?? 720,
    max_days_ahead: calendar?.max_days_ahead ?? 30,
    timezone: calendar?.timezone || "America/New_York",
    host_user_id: calendar?.host_user_id ? String(calendar.host_user_id) : user ? String(user.id) : "",
    google_calendar_id: calendar?.google_calendar_id || "primary",
    location_kind: calendar?.location_kind || "google_meet",
    location_text: calendar?.location_text || "",
    active: calendar?.active ?? true,
  }));
  const [hours, setHours] = useState<WeeklyRange[]>(calendar?.weekly_hours?.length ? calendar.weekly_hours : [1, 2, 3, 4, 5].map((day) => ({ day, start: "09:00", end: "17:00" })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  const dayRanges = (day: number) => hours.filter((r) => r.day === day);
  const toggleDay = (day: number, on: boolean) => setHours((h) => (on ? [...h, { day, start: "09:00", end: "17:00" }] : h.filter((r) => r.day !== day)));
  const setRange = (day: number, i: number, patch: Partial<WeeklyRange>) => setHours((h) => {
    let n = -1;
    return h.map((r) => (r.day === day && ++n === i ? { ...r, ...patch } : r));
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const body = { ...f, host_user_id: f.host_user_id || null, weekly_hours: hours, slug: f.slug || undefined };
      await api(calendar ? `/api/crm/calendars/${calendar.id}` : "/api/crm/calendars", { method: calendar ? "PUT" : "POST", body: JSON.stringify(body) });
      onSaved(calendar ? `${f.name} saved.` : `${f.name} added. Pick it on a form's After submit tab, or share its booking page.`);
    } catch (err) { setError(err instanceof Error ? err.message : "The calendar did not save."); }
    finally { setBusy(false); }
  }

  return (
    <Dialog title={calendar ? calendar.name : "New calendar"} onClose={onClose}>
      <form className="operation-form crm-edit" onSubmit={submit}>
        <div className="crm-edit-grid">
          <label>Name<input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required /></label>
          <label>Length
            <select value={f.duration_min} onChange={(e) => set("duration_min", Number(e.target.value))}>{LENGTHS.map((m) => <option key={m} value={m}>{m} minutes</option>)}</select>
          </label>
          <label className="wide"><span>Description <span className="crm-muted">Optional, shown on the booking page</span></span><textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} maxLength={1000} /></label>
          <label>Host
            <select value={f.host_user_id} onChange={(e) => set("host_user_id", e.target.value)}>
              <option value="">None</option>
              {(people.data || []).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </label>
          <label>Time zone
            <select value={f.timezone} onChange={(e) => set("timezone", e.target.value)}>{(ZONES.includes(f.timezone) ? ZONES : [f.timezone, ...ZONES]).map((z) => <option key={z} value={z}>{tzLabel(z)}</option>)}</select>
          </label>
        </div>

        <fieldset className="crm-hours">
          <legend>Hours</legend>
          {[1, 2, 3, 4, 5, 6, 0].map((day) => {
            const ranges = dayRanges(day);
            return (
              <div key={day} className="crm-hours-row">
                <label className="crm-check"><input type="checkbox" checked={ranges.length > 0} onChange={(e) => toggleDay(day, e.target.checked)} />{DAYS[day]}</label>
                {ranges.length ? (
                  <span className="crm-hours-ranges">
                    {ranges.map((r, i) => (
                      <span key={i} className="crm-hours-range">
                        <input type="time" aria-label={`${DAYS[day]} start`} value={r.start} step={900} onChange={(e) => setRange(day, i, { start: e.target.value })} />
                        <span aria-hidden="true">to</span>
                        <input type="time" aria-label={`${DAYS[day]} end`} value={r.end} step={900} onChange={(e) => setRange(day, i, { end: e.target.value })} />
                      </span>
                    ))}
                  </span>
                ) : <span className="crm-muted">Closed</span>}
              </div>
            );
          })}
        </fieldset>

        <div className="crm-edit-grid">
          <label>Gap before a call
            <select value={f.buffer_before_min} onChange={(e) => set("buffer_before_min", Number(e.target.value))}>{[0, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m ? `${m} minutes` : "None"}</option>)}</select>
          </label>
          <label>Gap after a call
            <select value={f.buffer_after_min} onChange={(e) => set("buffer_after_min", Number(e.target.value))}>{[0, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m ? `${m} minutes` : "None"}</option>)}</select>
          </label>
          <label>Earliest booking
            <select value={f.min_notice_min} onChange={(e) => set("min_notice_min", Number(e.target.value))}>{NOTICE.map((n) => <option key={n.v} value={n.v}>{n.v ? `${n.l} ahead` : n.l}</option>)}</select>
          </label>
          <label>Furthest booking
            <select value={f.max_days_ahead} onChange={(e) => set("max_days_ahead", Number(e.target.value))}>{AHEAD.map((d) => <option key={d} value={d}>{d} days out</option>)}</select>
          </label>
          <label>Where
            <select value={f.location_kind} onChange={(e) => set("location_kind", e.target.value as typeof f.location_kind)}>
              <option value="google_meet">Google Meet (link in the invite)</option>
              <option value="phone">Phone (you call them)</option>
              <option value="custom">Somewhere else</option>
            </select>
          </label>
          {f.location_kind === "custom" && <label>Place or link<input value={f.location_text} onChange={(e) => set("location_text", e.target.value)} maxLength={300} placeholder="Zoom link or address" /></label>}
          {google?.allowed && google.calendars?.length ? (
            <label>Google calendar
              <select value={f.google_calendar_id} onChange={(e) => set("google_calendar_id", e.target.value)}>
                {google.calendars.map((c) => <option key={c.id} value={c.primary ? "primary" : c.id}>{c.name}{c.primary ? " (main)" : ""}</option>)}
              </select>
            </label>
          ) : null}
          <label>Link name<span className="crm-slug"><span>/book/</span><input value={f.slug} placeholder={f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 60))} /></span></label>
          <label className="crm-check wide"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />Taking bookings</label>
        </div>
        {error && <p className="crm-error" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy}>{busy ? "Saving…" : calendar ? "Save calendar" : "Add calendar"}</button>
        </div>
      </form>
    </Dialog>
  );
}
